import React, { memo, useMemo } from 'react'
import Card from 'transfix-ui/components/Card'
import { formatRelativeTime } from 'transfix-ui/components/RelativeDateTime'
import { makeStyles, Typography } from 'transfix-ui/core'
import { ICarrierBidSourceTypes } from 'types/graphqlTypes'
import { formatApprovalState } from 'apps/marketplace/components/BidApprovalStateText'
import { CARRIER_SOURCE_LABELS } from 'apps/marketplace/components/CarrierOptionsSidePanel/types'
import DISPLAY from 'constants/display'
import { ICarrierOptionDetailsFragment } from '../../graphql/CarrierOptionDetailsFragment'
import CarrierOptionsCardRow from '../CarrierOptionCardRow/CarrierOptionsCardRow'
import CarrierOptionStatusDot from '../CarrierOptionStatusDot/CarrierOptionStatusDot'
import { TEST_IDS } from './constants'
import { getSourceChannelLabel, getSourceSegmentLabel } from './utils'

export interface ICarrierOptionsDetailsCardProps {
  option?: ICarrierOptionDetailsFragment
  loading: boolean
}

const useCarrierOptionsDetailsStyles = makeStyles(theme => ({
  statusValue: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('lg')]: {
      alignItems: 'flex-end',
    },
  },
  textRight: {
    [theme.breakpoints.up('lg')]: {
      textAlign: 'end',
      overflowWrap: 'break-word',
    },
    [theme.breakpoints.down('lg')]: {
      flexShrink: 0,
    },
  },
  statusDot: {
    whiteSpace: 'nowrap',
  },
  approval: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'end',
  },
}))

const CarrierOptionsDetailsCard = memo<ICarrierOptionsDetailsCardProps>(({ loading, option }) => {
  const classes = useCarrierOptionsDetailsStyles()

  const {
    createdAt,
    declineReason,
    latestBidApproval,
    source,
    sourceChannel,
    sourceSegment,
    status,
  } = option || {}

  const isDeclined = status === 'declined'

  const showSource = useMemo(() => {
    if (!source) {
      return false
    }
    return [
      ICarrierBidSourceTypes.fleet_planner,
      ICarrierBidSourceTypes.mobile,
      ICarrierBidSourceTypes.manual,
    ].includes(source)
  }, [source])

  const sourceLabel = useMemo(() => {
    if (!sourceChannel || !sourceSegment) {
      return ''
    }
    if (source === ICarrierBidSourceTypes.manual) {
      return `${getSourceChannelLabel(sourceChannel)} - ${getSourceSegmentLabel(sourceSegment)}`
    }
    return `${getSourceSegmentLabel(sourceSegment)}`
  }, [source, sourceChannel, sourceSegment])

  const { state: approvalState, updatedAt: approvalUpdatedAt } = latestBidApproval || {}

  const showOptionApprovalDetails = latestBidApproval || loading

  return (
    <Card>
      <Typography variant='h3'>Details</Typography>

      {/* Added Row */}
      <CarrierOptionsCardRow
        title='Added'
        value={
          <Typography variant='subtitle1' component='div' data-test={TEST_IDS.optionAddedDate}>
            {createdAt && <>{formatRelativeTime({ datetime: createdAt, isShorthand: true })} ago</>}
          </Typography>
        }
        loading={loading}
      />

      {/* Status Row */}
      <CarrierOptionsCardRow
        title='Status'
        value={
          <div className={classes.statusValue}>
            <div className={classes.statusDot}>
              <CarrierOptionStatusDot showLabel status={status} />
            </div>
            {isDeclined && declineReason && (
              <div className={classes.textRight} data-test={TEST_IDS.optionDeclinedReason}>
                <Typography variant='body2' component='div' color='textSecondary'>
                  {declineReason}
                </Typography>
              </div>
            )}
          </div>
        }
        loading={loading}
      />

      {/* Source Row */}
      <CarrierOptionsCardRow
        title='Source'
        value={
          <div className={classes.textRight}>
            {source ? (
              <>
                <Typography variant='subtitle1' component='div' data-test={TEST_IDS.optionSource}>
                  {CARRIER_SOURCE_LABELS[source]}
                </Typography>
                {showSource && (
                  <Typography
                    variant='body2'
                    component='div'
                    color='textSecondary'
                    data-test={TEST_IDS.optionSourceSegment}>
                    {sourceLabel}
                  </Typography>
                )}
              </>
            ) : (
              DISPLAY.empty
            )}
          </div>
        }
        loading={loading}
      />

      {/* Lead Approval Row */}
      {showOptionApprovalDetails && (
        <>
          <hr />
          <CarrierOptionsCardRow
            title='Lead Approval'
            value={
              <div className={classes.approval}>
                <Typography variant='subtitle1' component='div' data-test={TEST_IDS.optionApproval}>
                  {formatApprovalState(approvalState)}
                </Typography>
                <Typography
                  variant='body2'
                  component='div'
                  color='textSecondary'
                  data-test={TEST_IDS.optionApprovalDate}>
                  {approvalUpdatedAt && (
                    <>
                      {formatRelativeTime({ datetime: approvalUpdatedAt, isShorthand: true })} ago
                    </>
                  )}
                </Typography>
              </div>
            }
            loading={loading}
          />
        </>
      )}
    </Card>
  )
})

export default CarrierOptionsDetailsCard
